import { useState } from "react";
import { Button, Modal, DialogCancel } from "@cntrl/ui";
import { useShowMembers, useInviteMembers } from "../../app/hooks/useShows.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { CrewInviteePicker } from "../dashboard/CrewInviteePicker.js";
import styles from "./InviteMembersModal.module.scss";

export interface InviteMembersModalProps {
  showId: string;
  onClose: () => void;
}

/**
 * The show preview's "Invite members" quick-add — pick crew off the band's roster and send them the show
 * push. It is the same `CrewInviteePicker` + `useInviteMembers` the editor's Members section uses, in a
 * modal, so the preview's row action (ART-92 put it there) actually invites CREW rather than opening the
 * support-act finder it had been mis-wired to.
 *
 * It is band-scoped by construction: the roster, the picker's candidates and the invite all resolve against
 * the mounted band, so a GUEST invites its OWN crew onto a shared show (per-band members, ART-165) exactly as
 * an owner invites theirs — never the host's.
 */
export function InviteMembersModal({ showId, onClose }: InviteMembersModalProps) {
  const members = useShowMembers(showId);
  const invite = useInviteMembers();
  const [staged, setStaged] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Anyone already on the show is not offered again — the picker excludes them (the editor's rule).
  const onRoster = (members.data?.items ?? []).map((m) => m.personId).filter((id): id is string => !!id);

  function onInvite() {
    if (staged.length === 0) return;
    setError(null);
    invite.mutate(
      { showId, members: staged.map((personId) => ({ personId })) },
      {
        onSuccess: onClose,
        onError: (e) => setError(errorMessage(e, "Couldn't invite those members.")),
      },
    );
  }

  return (
    <Modal
      title="Invite members"
      size="md"
      onClose={onClose}
      footer={
        <div className={styles.footer}>
          {error && (
            <span className={styles.error} role="alert">
              {error}
            </span>
          )}
          <DialogCancel />
          <Button
            onClick={onInvite}
            disabled={staged.length === 0}
            loading={invite.isPending}
          >
            {staged.length > 0 ? `Invite ${staged.length}` : "Invite"}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <CrewInviteePicker
          value={staged}
          onChange={setStaged}
          exclude={onRoster}
          label="Invite crew"
          searchPlaceholder="Search your crew…"
          source="invitable"
          excludeOwner
        />
        <p className={styles.hint}>They get a push notification for this show.</p>
      </div>
    </Modal>
  );
}
